import JSZip from 'jszip'
import { isDeadObservation, normalizeRing, uppercaseText } from './domain'
import type { BirdAppearance, BirdParentSummary, BirdSearchResult } from './types'

function decodeXml(value: string) {
  return value
    .replace(/&#(\d+);/g, (_, code) => String.fromCodePoint(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, code) => String.fromCodePoint(Number.parseInt(code, 16)))
    .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
    .replace(/&gt;/g, '>').replace(/&lt;/g, '<').replace(/&amp;/g, '&')
}

function textFromXml(fragment: string) {
  return decodeXml([...fragment.matchAll(/<(?:\w+:)?t\b[^>]*>([\s\S]*?)<\/(?:\w+:)?t>/g)].map(match => match[1]).join(''))
}

function relationshipTarget(rels: string, id: string) {
  const tag = [...rels.matchAll(/<Relationship\s+([^>]*?)\/>/g)].find(match => match[1].includes(`Id="${id}"`))
  const target = tag?.[1].match(/Target="([^"]+)"/)?.[1]
  if (!target) return ''
  return target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`
}

function columnIndex(reference: string) {
  const letters = reference.match(/[A-Z]+/)?.[0] || ''
  return letters.split('').reduce((total, letter) => total * 26 + letter.charCodeAt(0) - 64, 0) - 1
}

function readRows(xml: string, sharedStrings: string[]) {
  const rows: string[][] = []
  for (const row of xml.matchAll(/<(?:\w+:)?row\b[^>]*>([\s\S]*?)<\/(?:\w+:)?row>/g)) {
    const values = Array(11).fill('')
    for (const cell of row[1].matchAll(/<(?:\w+:)?c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/(?:\w+:)?c>)/g)) {
      const reference = cell[1].match(/\br="([A-Z]+\d+)"/)?.[1]
      if (!reference) continue
      const index = columnIndex(reference)
      if (index < 0 || index >= values.length) continue
      const type = cell[1].match(/\bt="([^"]+)"/)?.[1] || ''
      const body = cell[2] || ''
      const raw = body.match(/<(?:\w+:)?v>([\s\S]*?)<\/(?:\w+:)?v>/)?.[1] || ''
      values[index] = type === 's' ? sharedStrings[Number(raw)] || '' : type === 'inlineStr' ? textFromXml(body) : decodeXml(raw)
    }
    rows.push(values)
  }
  return rows
}

export async function parseExcelBirds(bytes: ArrayBuffer | Uint8Array): Promise<BirdSearchResult[]> {
  const zip = await JSZip.loadAsync(bytes)
  const workbookXml = await zip.file('xl/workbook.xml')?.async('string')
  const relsXml = await zip.file('xl/_rels/workbook.xml.rels')?.async('string')
  if (!workbookXml || !relsXml) throw new Error('El archivo no parece ser un Excel válido.')

  const sharedXml = await zip.file('xl/sharedStrings.xml')?.async('string')
  const sharedStrings = sharedXml
    ? [...sharedXml.matchAll(/<(?:\w+:)?si\b[^>]*>([\s\S]*?)<\/(?:\w+:)?si>/g)].map(match => textFromXml(match[1]))
    : []

  const birds = new Map<string, BirdSearchResult>()
  let sequence = 0
  const addBird = (
    role: BirdAppearance['role'], anio: number, hoja: number,
    nombre: string, anilla: string, plumaje: string, sexo = '', observaciones = '',
    padre?: BirdParentSummary, madre?: BirdParentSummary
  ) => {
    const cleanName = uppercaseText(nombre.trim())
    const cleanRing = normalizeRing(anilla)
    const cleanPlumage = uppercaseText(plumaje.trim())
    const cleanSex = uppercaseText(sexo.trim())
    const cleanObservations = uppercaseText(observaciones.trim())
    if (!cleanName && !cleanRing && !cleanPlumage && !cleanSex && !cleanObservations) return
    const key = cleanRing ? `R:${cleanRing}|N:${cleanName}` : `ROW:${anio}:${hoja}:${role}:${sequence++}`
    const appearance: BirdAppearance = { sheetId: '', anio, hoja, role, observaciones: cleanObservations, deceased: isDeadObservation(cleanObservations), padre, madre }
    const found = birds.get(key)
    if (found) {
      found.appearances.push(appearance)
      if (!found.plumaje) found.plumaje = cleanPlumage
      if (!found.sexo) found.sexo = cleanSex
      if (!found.observaciones) found.observaciones = cleanObservations
      return
    }
    birds.set(key, {
      id: `excel-${sequence++}-${anio}-${hoja}`,
      nombre: cleanName,
      anilla: cleanRing,
      plumaje: cleanPlumage,
      sexo: cleanSex,
      observaciones: cleanObservations,
      appearances: [appearance],
      source: 'EXCEL'
    })
  }

  for (const sheet of workbookXml.matchAll(/<(?:\w+:)?sheet\b([^>]*?)\/>/g)) {
    const name = sheet[1].match(/\bname="([^"]+)"/)?.[1] || ''
    if (!/^\d{4}$/.test(name)) continue
    const relationId = sheet[1].match(/\br:id="([^"]+)"/)?.[1] || ''
    const path = relationshipTarget(relsXml, relationId)
    const xml = path ? await zip.file(path)?.async('string') : undefined
    if (!xml) continue

    let currentYear = Number(name)
    let currentSheet = 0
    let currentPadre: BirdParentSummary | undefined
    let currentMadre: BirdParentSummary | undefined
    for (const values of readRows(xml, sharedStrings)) {
      const pair = values[0].trim().match(/^(\d+)\s*\/\s*(\d{4})$/)
      if (pair) {
        currentSheet = Number(pair[1])
        currentYear = Number(pair[2])
        currentPadre = {
          nombre: uppercaseText(values[1]),
          anilla: normalizeRing(values[2]),
          plumaje: uppercaseText(values[3])
        }
        currentMadre = {
          nombre: uppercaseText(values[4]),
          anilla: normalizeRing(values[5]),
          plumaje: uppercaseText(values[6])
        }
        addBird('PADRE', currentYear, currentSheet, values[1], values[2], values[3])
        addBird('MADRE', currentYear, currentSheet, values[4], values[5], values[6])
      }
      if (currentSheet && values.slice(7, 11).some(Boolean)) {
        addBird('PICHÓN', currentYear, currentSheet, '', values[7], values[8], values[9], values[10], currentPadre, currentMadre)
      }
    }
  }

  return [...birds.values()]
}

export function filterExcelBirds(birds: BirdSearchResult[], query: string) {
  const value = uppercaseText(query.trim())
  const compact = normalizeRing(value).replace(/[^A-Z0-9]/g, '')
  if (!value) return []
  return birds.filter(bird =>
    uppercaseText(bird.nombre).includes(value)
    || uppercaseText(bird.anilla).includes(value)
    || bird.anilla.replace(/[^A-Z0-9]/g, '').includes(compact)
  ).slice(0, 100)
}
